import React, { useEffect, useMemo, useRef, useState } from 'react'
import Parser from 'html-react-parser'
import posthog from 'posthog-js'
import { useTranslation } from 'react-i18next'
import { extractDefinition } from 'utils/utils'
import { getWordTypeClass, removeSpaceBetweenWords, renderConverterWord, wordSimplified } from 'utils/helperFunction'
import { wordClick } from '../../redux/actions/storyAction'
import { AppUseSelector } from '../../redux/hooks'
import { Carousel } from 'react-bootstrap'
import { useWordAudio, hasAudioRecording, AudioSetting } from '../../hooks/useWordAudio'
import { AudioPlayIcon } from '../icons/icons'
import PocketLink from '../word-pocket/PocketLink'
import { chineseLang } from '../../i18n/chinese'
import { useWordPocketEnabled } from '../word-pocket/useWordPocketEnabled'
import { LocalizedText, meaningSenses, wordMeaning } from '../../i18n/localizedContent'
import ReportLink from './ReportLink'
import { lastSeenLabel } from './lastSeen'
import { fetchWordContext, WORD_CONTEXT_MESSAGE_KEYS, WordContextCode } from './wordContextRequest'
import { OccurrenceSense, Sense, alignedRenderings, senseOrder, wordSenseList } from '../../utils/wordSenses'
import './wordSenses.css'

/** The sentence the tapped word is in, with its translation (story reader only). */
export interface PanelSentence {
  index: number
  text: string
  /** The English, kept for the word pocket (it saves the English sentence). */
  en: string
  /** What to show: the learner-language translation when there is one, else the English. */
  translation?: LocalizedText
  /** ref_id for "Report this translation", null when the story payload has no story id. */
  refId?: string | null
}

// Server values shown as they come (mastery buckets, importance), translated by literal key.
const MASTERY_KEYS: Record<string, string> = {
  New: 'reader:wordPanel.masteryValues.new',
  Learning: 'reader:wordPanel.masteryValues.learning',
  Growing: 'reader:wordPanel.masteryValues.growing',
  Developing: 'reader:wordPanel.masteryValues.developing',
  Mastered: 'reader:wordPanel.masteryValues.mastered',
}
const IMPORTANCE_KEYS: Record<string, string> = {
  'Must learn': 'reader:wordPanel.importanceValues.mustLearn',
  'Should learn': 'reader:wordPanel.importanceValues.shouldLearn',
  'Nice to know': 'reader:wordPanel.importanceValues.niceToKnow',
}

const capture = (event: string, properties: Record<string, unknown>) => {
  try {
    posthog.capture(event, properties)
  } catch {
    /* analytics must never break the reader */
  }
}

/** Whether the learner asked to see the English meaning under a learner-language one. */
const useShowEnglishMeanings = () =>
  AppUseSelector((state: any) => state.user?.credentials?.customFields?.show_english_meanings === true)

const squashPinyin = (pinyin: string | null | undefined) => (pinyin || '').replace(/\s+/g, '').toLowerCase()

/**
 * The sense the tapped occurrence has in its sentence (context-matched senses), when it belongs to
 * the word the panel shows and the word has an inventory. Null otherwise.
 */
export const pickedSense = (wordDetails: any, contextSense: OccurrenceSense | null | undefined): number | null => {
  const senses = wordSenseList(wordDetails)
  if (!senses || !contextSense) return null
  if (wordSimplified(wordDetails?.word || '') !== contextSense.word) return null
  return contextSense.sense >= 0 && contextSense.sense < senses.length ? contextSense.sense : null
}

/** The pinyin under the headword: the picked sense's reading when there is one, else the word's. */
export const headwordPinyin = (wordDetails: any, picked: number | null): string | undefined => {
  const senses = wordSenseList(wordDetails)
  return picked != null && senses?.[picked]?.pinyin ? senses[picked].pinyin : wordDetails?.pinyin
}

/**
 * The word's senses as a list: the one it has in this sentence first and emphasised, the others
 * below in a quieter style, each with its reading when that differs from the headword's. In a
 * learner language, each sense's rendering (aligned with the inventory), with the English under it
 * when the learner asked for English meanings.
 */
const SenseList = ({
  senses,
  picked,
  renderings,
  lang,
  showEnglish,
  headPinyin,
}: {
  senses: Sense[]
  picked: number | null
  renderings: string[] | null
  lang: string
  showEnglish: boolean
  headPinyin?: string
}) => {
  const { t } = useTranslation('reader')
  return (
    <ol className={`word-senses${picked != null ? ' word-senses--picked' : ''}`} aria-label={t('wordPanel.senses.label')}>
      {senseOrder(senses.length, picked).map((i) => {
        const sense = senses[i]
        const isPicked = i === picked
        const showReading = !!sense.pinyin && squashPinyin(sense.pinyin) !== squashPinyin(headPinyin)
        return (
          <li key={i} className={`word-sense${isPicked ? ' word-sense--context' : ''}`}>
            {isPicked && <span className='visually-hidden'>{t('wordPanel.senses.inThisSentence')} </span>}
            {showReading && <span className='word-sense-pinyin'>{removeSpaceBetweenWords(sense.pinyin)}</span>}
            <span className='word-sense-meaning' lang={renderings ? lang : 'en'}>
              {renderings ? renderings[i] : sense.en}
            </span>
            {renderings && showEnglish && (
              <span className='word-sense-english' lang='en'>
                {sense.en}
              </span>
            )}
          </li>
        )
      })}
    </ol>
  )
}

/**
 * The word's meaning. With a sense inventory (`senses`), the senses as a list, the one picked for
 * this sentence first. Without one, as before: the learner-language gloss when the API sent one
 * (with the English under it when the learner asked for it), else the English alone.
 */
export const WordMeaning = ({
  wordDetails,
  allowReport = false,
  picked = null,
}: {
  wordDetails: any
  allowReport?: boolean
  picked?: number | null
}) => {
  const showEnglish = useShowEnglishMeanings()
  const { primary, english } = wordMeaning(wordDetails)
  const word = wordSimplified(wordDetails?.word || '')
  const report =
    allowReport && word && primary.localized ? (
      <div className='word-meaning-report'>
        <ReportLink kind='word' refId={word} lang={primary.lang} />
      </div>
    ) : null

  const senses = wordSenseList(wordDetails)
  const renderings = senses && primary.localized ? alignedRenderings(primary.text, senses.length) : null
  // A localized meaning that does not line up with the inventory is shown as a whole, as before.
  if (senses && (!primary.localized || renderings)) {
    return (
      <>
        <SenseList
          senses={senses}
          picked={picked}
          renderings={renderings}
          lang={primary.lang}
          showEnglish={showEnglish}
          headPinyin={headwordPinyin(wordDetails, picked)}
        />
        {report}
      </>
    )
  }

  if (!primary.localized) {
    return english ? (
      <p className='speck_text' lang='en'>
        {Parser(extractDefinition(english))}
      </p>
    ) : null
  }
  return (
    <>
      <p className='speck_text' lang={primary.lang}>
        {meaningSenses(primary.text).map((sense, i) => (
          <React.Fragment key={i}>
            {i > 0 && '; '}
            <span>{sense}</span>
          </React.Fragment>
        ))}
      </p>
      {showEnglish && english && (
        <p className='speck_text word-meaning-english' lang='en'>
          {Parser(extractDefinition(english))}
        </p>
      )}
      {report}
    </>
  )
}

interface IWordDetailProps {
  visible?: boolean
  setOpenExampleModal: any
  isMasteryOpen: boolean
  setMasteryOpen: any
  isImportanceOpen: boolean
  setImportanceOpen: any
  setWordDetailVisible: any
  setActiveIndex: any
  openExampleModal: any
  showMasterySection: boolean
  isAudioPlaying?: boolean
  /** The sentence the tapped word is in, with its translation (story reader only). */
  sentence?: PanelSentence | null
  /** The reader shows English already, so the sentence English starts open. */
  showEnglish?: boolean
  onSentenceRevealed?: (index: number) => void
  /** The sense the tapped occurrence has in its sentence (story reader only). */
  contextSense?: OccurrenceSense | null
}
interface ContextState {
  loader: boolean
  val: string
  /** Set when the answer is one of the API's fallbacks: its translated copy is shown instead. */
  code?: WordContextCode | null
  error: boolean
}
const EMPTY_CONTEXT: ContextState = { loader: false, val: '', code: null, error: false }

const downArrow = <i className='fa fa-angle-down' />
const upArrow = <i className='fa fa-angle-up' />

const WordDetail = React.forwardRef<HTMLDivElement, IWordDetailProps>(
  (
    {
      visible,
      setOpenExampleModal,
      isMasteryOpen,
      setMasteryOpen,
      isImportanceOpen,
      setImportanceOpen,
      showMasterySection,
      isAudioPlaying,
      sentence = null,
      showEnglish = false,
      onSentenceRevealed,
      setWordDetailVisible,
      contextSense = null,
    }: any,
    ref: any,
  ) => {
    const { wordDetailLoader, wordDetails, currentStory, storyDictionary, audio_pronunciation_setting } =
      AppUseSelector(({ story }) => story)
    const { credentials } = AppUseSelector(({ user }) => user)
    const characterPreference = credentials?.customFields?.character
    const pocketEnabled = useWordPocketEnabled()
    const { t } = useTranslation('reader')
    // The mobile carousel's dots and arrows, which react-bootstrap labels "Slide 1", "Previous"...
    const carouselLabels = {
      indicatorLabels: [t('wordPanel.slides.meaning'), t('wordPanel.slides.mastery')],
      prevLabel: t('wordPanel.slides.previous'),
      nextLabel: t('wordPanel.slides.next'),
    }
    const [expandContext, setExpandContext] = useState(false)
    const [contextState, setContextState] = useState<ContextState>(EMPTY_CONTEXT)
    const [wordChangeLoader, setWordChangeLoader] = useState({ loader: false, val: '' })
    // Derive directly from Redux so we never paint the previously-opened word's mastery
    // while waiting for a local useEffect to catch up. Optimistic updates from the
    // Mark as known/new buttons live in Redux now (wordClick dispatches SET_WORD_DETAIL).
    const wordType = wordDetails?.mastery
    const picked = pickedSense(wordDetails, contextSense)
    const headPinyin = headwordPinyin(wordDetails, picked)
    const wordTypeList = useMemo(
      () => getWordTypeClass(Number.isInteger(wordDetails?.mastery) ? 'word' : wordDetails?.mastery),
      [wordDetails?.mastery],
    )
    const { playWordAudio, isPlaying: isAutoPlaying } = useWordAudio()
    const lastPlayedWordRef = useRef<string | null>(null)
    // Combine parent isAudioPlaying prop with local auto-play state
    const combinedIsAudioPlaying = isAudioPlaying || isAutoPlaying

    // Auto-play audio when a new word is loaded (if audio setting is not 'none')
    const wordId = wordDetails?._id || storyDictionary?.[wordSimplified(wordDetails?.word)]?._id
    const wordHasAudio = wordDetails?.has_audio ?? storyDictionary?.[wordSimplified(wordDetails?.word)]?.has_audio
    useEffect(() => {
      if (
        visible &&
        wordId &&
        audio_pronunciation_setting !== 'none' &&
        hasAudioRecording(wordHasAudio) &&
        lastPlayedWordRef.current !== wordId
      ) {
        lastPlayedWordRef.current = wordId
        playWordAudio({ _id: wordId, has_audio: wordHasAudio })
      }
    }, [visible, wordId, wordHasAudio, audio_pronunciation_setting, playWordAudio])

    // Reset last played word when popup closes
    useEffect(() => {
      if (!visible) {
        lastPlayedWordRef.current = null
      }
    }, [visible])

    useEffect(() => {
      if (wordDetailLoader) {
        setExpandContext(false)
        setContextState(EMPTY_CONTEXT)
      }
    }, [wordDetailLoader])

    useEffect(() => {
      setExpandContext(false)
      setContextState(EMPTY_CONTEXT)
    }, [wordDetails?.word])

    // word_lookup, once per word opened in the panel, when its meaning has loaded.
    const trackedLookup = useRef<string | null>(null)
    useEffect(() => {
      if (!visible) {
        trackedLookup.current = null
        return
      }
      const word = wordSimplified(wordDetails?.word || '')
      if (wordDetailLoader || !word || trackedLookup.current === word) return
      trackedLookup.current = word
      const { primary } = wordMeaning(wordDetails)
      capture('word_lookup', {
        word,
        story_id: currentStory?.id,
        has_localized_meaning: primary.localized,
        meaning_language: primary.localized ? primary.lang : null,
      })
    }, [visible, wordDetailLoader, wordDetails])

    const [expandSentence, setExpandSentence] = useState(showEnglish)
    useEffect(() => {
      setExpandSentence(showEnglish)
    }, [sentence?.index, showEnglish])
    const sentenceProps = {
      sentence,
      expandSentence,
      toggleSentence: () => {
        if (!expandSentence && sentence) onSentenceRevealed?.(sentence.index)
        setExpandSentence(!expandSentence)
      },
    }

    useEffect(() => {
      const handleClickOutside = (event) => {
        const target = event.target as HTMLElement
        if (!target.closest('.modal_tooltip_div')) {
          setImportanceOpen(false)
          setMasteryOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    const handle = {
      wordClick: (word, word_status, wordName) => {
        setWordChangeLoader({ loader: true, val: word_status })
        wordClick({
          word: wordSimplified(word),
          word_status,
          storyId: currentStory?.id,
          mastery: wordDetails?.mastery,
        }).then((res) => {
          if (res) {
            setWordChangeLoader({ loader: false, val: '' })
            // wordClick action now patches storyDictionary + wordDetails with the fresh
            // mastery, so wordType updates automatically from Redux — no local state needed.
            setMasteryOpen(false)
          }
        })
      },
      getWordContext: async () => {
        setExpandContext(!expandContext)
        if (!contextState?.val) {
          setContextState({
            ...contextState,
            loader: true,
          })
          // Asks in the interface language (when it is a learner language). Only ever renders
          // strings: an object here (e.g. a serialized backend error) would blank the page.
          const answer = await fetchWordContext(wordDetails?.sentenceString, wordSimplified(wordDetails?.word))
          setContextState({
            val: answer?.text || '',
            code: answer?.code || null,
            loader: false,
            error: !answer,
          })
        }
      },
    }
    // Under the word's level: a small button that turns green once the word is in the pocket.
    const pocketControl = pocketEnabled ? (
      <PocketLink sentence={sentence} closePanel={() => setWordDetailVisible?.(false)} />
    ) : null

    return (
      <>
        <div ref={ref} className={`word_detail_modal_main_div ${visible ? 'bounce-in-right' : ' '}`}>
          {wordDetailLoader ? (
            <div className='word_detail_card_skeleton'>
              <div className='word_details_head_div'>
                <h5 className='big_word' lang={chineseLang(characterPreference)}>
                  {/* data-w lets a pocketed word carry its pocket mark here too. */}
                  <span data-w={wordSimplified(wordDetails?.word || '')}>
                    {renderConverterWord(wordDetails?.word, characterPreference)}
                  </span>
                </h5>
                <div className='word_head_side'>
                  <p className='small_word'>
                    {wordDetails?.test_level
                      ?.map((levelDetail) => `${levelDetail?.test} ${levelDetail?.level}`)
                      .join(' / ')}
                  </p>
                  {pocketControl}
                </div>
              </div>

              {/* Desktop loading layout */}
              <div className='word_detail_slider_desktop_div'>
                <div style={{ marginBottom: '24px' }}>
                  {headPinyin && <p className='speck_text'>{removeSpaceBetweenWords(headPinyin)}</p>}
                  <WordMeaning wordDetails={wordDetails} picked={picked} />
                </div>
                <InteractiveButtonsSection
                  handle={handle}
                  expandContext={expandContext}
                  contextState={contextState}
                  setOpenExampleModal={setOpenExampleModal}
                  wordDetails={wordDetails}
                />
                {showMasterySection && (
                  <>
                    <div className='divider_line'></div>
                    <div className='skeleton_mob_space'>
                      <div
                        className='para_skeleton skeleton_animation'
                        style={{ marginBottom: '16px', height: '20px' }}
                      ></div>
                      <div
                        className='para_skeleton skeleton_animation'
                        style={{ marginBottom: '17px', height: '45px' }}
                      ></div>
                      <div className='para_skeleton skeleton_animation' style={{ height: '19px' }}></div>
                    </div>
                  </>
                )}
              </div>

              {/* Mobile loading layout with carousel structure */}
              <div className='word_detail_slider_div'>
                <Carousel interval={null} indicators={showMasterySection && true} {...carouselLabels}>
                  <Carousel.Item>
                    <div className='first_slide_main_div'>
                      <div style={{ marginBottom: '24px' }}>
                        {headPinyin && <p className='speck_text'>{removeSpaceBetweenWords(headPinyin)}</p>}
                        <WordMeaning wordDetails={wordDetails} picked={picked} />
                      </div>
                      <InteractiveButtonsSection
                        handle={handle}
                        expandContext={expandContext}
                        contextState={contextState}
                        setOpenExampleModal={setOpenExampleModal}
                        wordDetails={wordDetails}
                      />
                    </div>
                  </Carousel.Item>
                  {showMasterySection && (
                    <Carousel.Item>
                      <div className='sec_slide_main_div'>
                        <div className='divider_line'></div>
                        <div className='skeleton_mob_space'>
                          <div
                            className='para_skeleton skeleton_animation'
                            style={{ marginBottom: '16px', height: '20px' }}
                          ></div>
                          <div
                            className='para_skeleton skeleton_animation'
                            style={{ marginBottom: '17px', height: '45px' }}
                          ></div>
                          <div className='para_skeleton skeleton_animation' style={{ height: '19px' }}></div>
                        </div>
                      </div>
                    </Carousel.Item>
                  )}
                </Carousel>
              </div>
            </div>
          ) : (
            wordDetails && (
              <>
                <div className='word_details_head_div'>
                  <h5 className='big_word' lang={chineseLang(characterPreference)}>
                    {/* data-w lets a pocketed word carry its pocket mark here too. */}
                    <span data-w={wordSimplified(wordDetails?.word || '')}>
                      {renderConverterWord(wordDetails?.word, characterPreference)}
                    </span>
                  </h5>
                  <div className='word_head_side'>
                    <p className='small_word'>
                      {wordDetails?.test_level
                        ?.map(
                          (levelDetail: { test: string; level: string }): string =>
                            `${levelDetail.test} ${levelDetail.level}`,
                        )
                        .join(' / ')}
                    </p>
                    {pocketControl}
                  </div>
                </div>
                <div className='word_detail_slider_desktop_div'>
                  <FirstSlide
                    wordDetails={wordDetails}
                    handle={handle}
                    expandContext={expandContext}
                    contextState={contextState}
                    setOpenExampleModal={setOpenExampleModal}
                    isAudioPlaying={combinedIsAudioPlaying}
                    sentenceProps={sentenceProps}
                    picked={picked}
                    headPinyin={headPinyin}
                  />
                  {showMasterySection && (
                    <SecondSlide
                      wordDetails={wordDetails}
                      setImportanceOpen={setImportanceOpen}
                      isImportanceOpen={isImportanceOpen}
                      wordType={wordType}
                      setMasteryOpen={setMasteryOpen}
                      isMasteryOpen={isMasteryOpen}
                      wordChangeLoader={wordChangeLoader}
                      handle={handle}
                      visible={visible}
                      wordTypeList={wordTypeList}
                    />
                  )}
                </div>
                <div className='word_detail_slider_div'>
                  <Carousel interval={null} indicators={showMasterySection && true} {...carouselLabels}>
                    <Carousel.Item>
                      <FirstSlide
                        wordDetails={wordDetails}
                        handle={handle}
                        expandContext={expandContext}
                        contextState={contextState}
                        setOpenExampleModal={setOpenExampleModal}
                        isAudioPlaying={combinedIsAudioPlaying}
                        sentenceProps={sentenceProps}
                        picked={picked}
                        headPinyin={headPinyin}
                      />
                    </Carousel.Item>
                    {showMasterySection && (
                      <Carousel.Item>
                        <SecondSlide
                          wordDetails={wordDetails}
                          setImportanceOpen={setImportanceOpen}
                          isImportanceOpen={isImportanceOpen}
                          wordType={wordType}
                          setMasteryOpen={setMasteryOpen}
                          isMasteryOpen={isMasteryOpen}
                          wordChangeLoader={wordChangeLoader}
                          handle={handle}
                          visible={visible}
                          wordTypeList={wordTypeList}
                        />
                      </Carousel.Item>
                    )}
                  </Carousel>
                </div>
              </>
            )
          )}
        </div>
      </>
    )
  },
)

interface InteractiveButtonsSectionProps {
  handle: {
    getWordContext: () => void
  }
  expandContext: boolean
  contextState: ContextState
  setOpenExampleModal: (open: boolean) => void
  wordDetails?: any
  sentenceProps?: {
    sentence: PanelSentence | null
    expandSentence: boolean
    toggleSentence: () => void
  }
}

const InteractiveButtonsSection = ({
  handle,
  expandContext,
  contextState,
  setOpenExampleModal,
  sentenceProps,
}: InteractiveButtonsSectionProps) => {
  const { t } = useTranslation('reader')
  const handleViewExamples = () => {
    setOpenExampleModal(true)
  }
  const sentence = sentenceProps?.sentence
  const translation: LocalizedText | null = sentence
    ? sentence.translation || { text: sentence.en, lang: 'en', localized: false }
    : null

  return (
    <div className='tooltip_text_main_div' style={{ marginBottom: '24px' }}>
      <div>
        <p className='modal_tooltip_text' onClick={() => handle.getWordContext()}>
          {t('wordPanel.explainInContext')} {expandContext ? upArrow : downArrow}
        </p>
        <>
          {expandContext &&
            (contextState?.loader ? (
              <div className='para_skeleton skeleton_animation mt-2'></div>
            ) : (
              <p className={`para_show_text ${expandContext ? 'fadeincls' : ''}`}>
                {contextState.code
                  ? t(WORD_CONTEXT_MESSAGE_KEYS[contextState.code])
                  : contextState.val || t('wordPanel.noData')}
              </p>
            ))}
        </>
      </div>
      {sentenceProps && sentence && translation && (
        <div>
          <p className='modal_tooltip_text' onClick={sentenceProps.toggleSentence}>
            {t('wordPanel.showSentenceTranslation')} {sentenceProps.expandSentence ? upArrow : downArrow}
          </p>
          {sentenceProps.expandSentence && (
            <>
              <p className='para_show_text fadeincls' lang={translation.lang}>
                {translation.text}
              </p>
              {translation.localized && sentence.refId && (
                <ReportLink kind='sentence' refId={sentence.refId} lang={translation.lang} />
              )}
            </>
          )}
        </div>
      )}

      <p
        className='modal_tooltip_text'
        onClick={handleViewExamples}
        style={{
          cursor: 'pointer',
        }}
      >
        {t('wordPanel.viewExamples')}
      </p>
    </div>
  )
}

const FirstSlide = ({
  wordDetails,
  handle,
  expandContext,
  contextState,
  setOpenExampleModal,
  isAudioPlaying,
  sentenceProps = undefined,
  picked = null,
  headPinyin = undefined,
}: any) => {
  const { t } = useTranslation('reader')
  const { playWordAudio, isPlaying: localIsPlaying } = useWordAudio()
  const { storyDictionary, audio_pronunciation_setting } = AppUseSelector(({ story }) => story)

  // Use wordDetails directly for audio (works on all pages), fallback to storyDictionary for backwards compatibility
  const wordData = wordDetails?._id
    ? { _id: wordDetails._id, has_audio: wordDetails.has_audio }
    : storyDictionary?.[wordSimplified(wordDetails?.word)]
  const showAudioButton = wordData && hasAudioRecording(wordData.has_audio)

  // Show playing state if either parent or local audio is playing
  const isPlaying = isAudioPlaying || localIsPlaying

  const handleAudioClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!wordData) return

    // If setting is 'none', clicking button plays both voices
    const setting: AudioSetting | undefined = audio_pronunciation_setting === 'none' ? 'both' : undefined
    playWordAudio(wordData, setting)
  }

  return (
    <>
      <div className='first_slide_main_div'>
        <div style={{ marginBottom: '24px' }}>
          {headPinyin && (
            <div className='pinyin-audio-row'>
              <p className='speck_text' style={{ margin: 0 }}>
                {removeSpaceBetweenWords(headPinyin)}
              </p>
              {showAudioButton && (
                <button
                  className={`word-audio-btn${isPlaying ? ' playing' : ''}`}
                  onClick={handleAudioClick}
                  disabled={isPlaying}
                  title={t('wordPanel.playPronunciation')}
                >
                  <AudioPlayIcon playing={isPlaying} />
                </button>
              )}
            </div>
          )}
          <WordMeaning wordDetails={wordDetails} allowReport picked={picked} />
        </div>
        <InteractiveButtonsSection
          handle={handle}
          expandContext={expandContext}
          contextState={contextState}
          setOpenExampleModal={setOpenExampleModal}
          wordDetails={wordDetails}
          sentenceProps={sentenceProps}
        />
      </div>
    </>
  )
}

const SecondSlide = ({
  wordDetails,
  setImportanceOpen,
  isImportanceOpen,
  wordType,
  setMasteryOpen,
  isMasteryOpen,
  wordChangeLoader,
  handle,
  visible,
  wordTypeList,
}) => {
  const { t } = useTranslation('reader')
  return (
    <>
      <div className='divider_line'></div>
      <div className='sec_slide_main_div'>
        {/* slider code end */}
        {wordDetails?.importance && (
          <p className='word_detail_small_text position-relative'>
            {t('wordPanel.importance')}
            <span className='bold_text'>
              {IMPORTANCE_KEYS[wordDetails?.importance] ? t(IMPORTANCE_KEYS[wordDetails.importance]) : wordDetails?.importance}
              <img
                className='ms-2 curser_pointer'
                style={{ position: 'relative', top: '-1px', cursor: 'pointer' }}
                alt={t('wordPanel.iconAlt')}
                src={'/images/help-circle.svg'}
                onClick={() => setImportanceOpen(!isImportanceOpen)}
              />
            </span>
            {isImportanceOpen ? (
              <div className='modal_tooltip_div'>
                <p className='tooltip_para_text'>{t('wordPanel.importanceHelp')}</p>
              </div>
            ) : null}
          </p>
        )}
        <div>
          {!Number.isInteger(wordType) && (
            <p className='word_detail_small_text position-relative' style={{ marginBottom: '10px' }}>
              {t('wordPanel.mastery')}
              <span className='bold_text'>
                {MASTERY_KEYS[wordType] ? t(MASTERY_KEYS[wordType]) : wordType}
                <img
                  className='ms-2'
                  style={{ position: 'relative', top: '-1px', cursor: 'pointer' }}
                  alt={t('wordPanel.iconAlt')}
                  src={'/images/help-circle.svg'}
                  onClick={() => setMasteryOpen(!isMasteryOpen)}
                />
              </span>
              {isMasteryOpen ? (
                <div className={`modal_tooltip_div ${visible ? 'actions' : ' '}`}>
                  <p className='tooltip_para_text'>{t('wordPanel.masteryHelp')}</p>
                  <div className='tooltip_btn_div'>
                    <button
                      className='btn tooltip_btn'
                      disabled={wordChangeLoader?.loader || wordType === 'Mastered'}
                      onClick={() => handle.wordClick(wordDetails?.word, 'known', 'Mastered')}
                    >
                      {wordChangeLoader?.loader && wordChangeLoader?.val === 'known'
                        ? t('wordPanel.loading')
                        : t('wordPanel.markMastered')}
                    </button>
                    <button
                      className='btn tooltip_btn'
                      disabled={wordChangeLoader?.loader || wordType === 'New'}
                      onClick={() => handle.wordClick(wordDetails?.word, 'new', 'New')}
                    >
                      {wordChangeLoader?.loader && wordChangeLoader?.val === 'new'
                        ? t('wordPanel.loading')
                        : t('wordPanel.markNew')}
                    </button>
                  </div>
                </div>
              ) : null}
            </p>
          )}
          {wordTypeList && (
            <div className='process_box_main_div'>
              {wordTypeList?.map((className, i) => <div className={`process_box ${className}`} key={i}></div>)}
            </div>
          )}
        </div>
        {wordDetails?.struggling && (
          <p className='word_detail_small_text'>
            {t('wordPanel.struggling')}
            <span className='bold_text word_detail_struggle_value'>
              {wordDetails?.lookups > 1
                ? t('wordPanel.lookedUp', { count: wordDetails.lookups })
                : t('wordPanel.keepsNeedingLookup')}
            </span>
          </p>
        )}
        {wordDetails?.last_seen && wordType !== 'New' && (
          <p className='word_detail_small_text mb-0'>
            {t('wordPanel.lastSeen', { time: lastSeenLabel(wordDetails?.last_seen_at, wordDetails?.last_seen) })}
          </p>
        )}
      </div>
    </>
  )
}

export default WordDetail
